import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import ForgotPasswordModal from '@/components/ForgotPasswordModal';
import LoginModal from '@/components/LoginModal';
import RegisterModal from '@/components/RegisterModal';

type AuthView = 'login' | 'register' | 'forgot';

type MandatoryAuthContextValue = {
  mandatoryBlocking: boolean;
};

const MandatoryAuthContext = createContext<MandatoryAuthContextValue>({
  mandatoryBlocking: false,
});

export function useMandatoryAuthBlocking(): boolean {
  return useContext(MandatoryAuthContext).mandatoryBlocking;
}

/**
 * Exige login em qualquer rota: enquanto não houver `authToken`, exibe modais
 * bloqueados (sem fechar pelo overlay). O Header não renderiza os mesmos modais
 * quando `mandatoryBlocking` está ativo, para evitar duplicação.
 */
export function MandatoryAuthProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [hasToken, setHasToken] = useState(
    () => !!localStorage.getItem('authToken')
  );
  const [authView, setAuthView] = useState<AuthView>('login');
  const hadTokenRef = useRef(!!localStorage.getItem('authToken'));

  const refresh = useCallback(() => {
    setHasToken(!!localStorage.getItem('authToken'));
  }, []);

  useEffect(() => {
    const onLoggedIn = () => {
      refresh();
    };
    const onOpenLogin = () => {
      setHasToken(false);
      setAuthView('login');
    };
    const onStorage = (e: StorageEvent) => {
      if (e.key === 'authToken' || e.key === null) {
        refresh();
      }
    };
    window.addEventListener('user-logged-in', onLoggedIn);
    window.addEventListener('open-login', onOpenLogin);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener('user-logged-in', onLoggedIn);
      window.removeEventListener('open-login', onOpenLogin);
      window.removeEventListener('storage', onStorage);
    };
  }, [refresh]);

  // Ao perder sessão (ex.: 401 + open-login), volta para a tela de login do gate
  useEffect(() => {
    if (hadTokenRef.current && !hasToken) {
      setAuthView('login');
    }
    hadTokenRef.current = hasToken;
  }, [hasToken]);

  const ctx = useMemo(
    () => ({ mandatoryBlocking: !hasToken }),
    [hasToken]
  );

  const closeForgotToLogin = useCallback(() => {
    setAuthView('login');
  }, []);

  return (
    <MandatoryAuthContext.Provider value={ctx}>
      {children}
      {!hasToken && (
        <>
          <LoginModal
            isOpen={authView === 'login'}
            mandatory
            onClose={() => {
              /* sucesso chama handleClose internamente; mandatory não fecha pelo overlay */
            }}
            onOpenRegister={() => {
              setAuthView('register');
            }}
            onOpenForgotPassword={() => {
              setAuthView('forgot');
            }}
          />
          <RegisterModal
            isOpen={authView === 'register'}
            mandatory
            onClose={() => {}}
            onOpenLogin={() => {
              setAuthView('login');
            }}
          />
          <ForgotPasswordModal
            isOpen={authView === 'forgot'}
            mandatory
            onClose={closeForgotToLogin}
            onBackToLogin={() => {
              setAuthView('login');
            }}
          />
        </>
      )}
    </MandatoryAuthContext.Provider>
  );
}

export default MandatoryAuthProvider;
